import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  clearResearchAssistantIsProcessing,
  completeProgressSteps,
  markWorkflowCompletedProgress,
  selectResearchAssistantIsWaitingForResponse,
  setResearchAssistantIsProcessing,
  setWorkflowId,
} from '@verantos-dev/app-state-research-assistant';
import type { WorkflowStartedEvent, WorkflowCompletedEvent } from '@verantos-dev/types-common';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';

/** Grace period (ms) beyond server timeout before the frontend forces UI unlock. */
const GRACE_PERIOD_MS = 15_000;

/**
 * Safety timer that auto-unlocks the UI if the backend's workflow_completed
 * event never arrives (e.g. backend crash, network partition).
 *
 * The timer is seeded from the server's `timeout_at` timestamp so the frontend
 * and backend share a single source of truth for the workflow deadline.
 *
 * Also handles the `workflow_started` event to block all connected clients
 * (not just the one that sent the message) from editing while a workflow runs.
 *
 * Listeners are always registered — the backend gates whether to emit these
 * events behind the `analytical_variables_in_cohort_builder` feature flag.
 * If the flag is off server-side, no events are sent and these listeners are inert.
 *
 * TODO: cleanup with analytical_variables_in_cohort_builder ff — remove this comment
 */
export const useWorkflowTimeoutGuard = () => {
  const dispatch = useAppDispatch();
  const { socket: currentSocket } = useResearchAssistantSocketContext();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isProcessing = useAppSelector(selectResearchAssistantIsWaitingForResponse);
  // Track isProcessing in a ref so the callback doesn't recreate on every state change
  const isProcessingRef = useRef(isProcessing);
  isProcessingRef.current = isProcessing;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  /** Start (or restart) the safety timer based on the server's timeout_at. */
  const startTimer = useCallback(
    (workflowId: string, timeoutAt: string, timeoutSeconds: number) => {
      clearTimer();

      const serverDeadline = new Date(timeoutAt).getTime();
      const remaining = serverDeadline - Date.now();
      // Use the larger of remaining + grace or a pure-duration fallback (handles clock skew)
      const delay = Math.max(remaining + GRACE_PERIOD_MS, (timeoutSeconds + 5) * 1_000);

      timerRef.current = setTimeout(() => {
        console.warn(
          `[WorkflowTimeoutGuard] Safety timeout fired for workflow ${workflowId} — forcing UI unlock`
        );
        dispatch(clearResearchAssistantIsProcessing({ transactionId: workflowId }));
        dispatch(completeProgressSteps());
      }, delay);
    },
    [clearTimer, dispatch]
  );

  const onWorkflowStarted = useCallback(
    (data: WorkflowStartedEvent) => {
      console.info('[WorkflowTimeoutGuard] workflow_started received:', {
        workflow_id: data.workflow_id,
        has_user_prompt: !!data.user_prompt,
      });
      const wasAlreadyProcessing = isProcessingRef.current;

      // Lock the UI for ALL clients in the room — the originating client already
      // has isProcessing=true from the chat submission flow, but we re-set it with
      // the server-assigned workflow_id so cleanup uses the canonical ID.
      dispatch(setResearchAssistantIsProcessing({ transactionId: data.workflow_id }));
      dispatch(setWorkflowId(data.workflow_id));

      // Show the user prompt for non-originating clients. The originating client
      // already added the message to Redux before emitting 'publish', so skip it
      // to avoid duplication.
      if (!wasAlreadyProcessing && data.user_prompt) {
        dispatch(
          addResearchAssistantMessages([
            {
              message: data.user_prompt,
              currentUser: 'user',
              isCurrentUser: false,
              timestamp: new Date().toISOString(),
            },
          ])
        );
      }

      startTimer(data.workflow_id, data.timeout_at, data.timeout_seconds);
    },
    [dispatch, startTimer]
  );

  const onWorkflowCompleted = useCallback(
    (_data: WorkflowCompletedEvent) => {
      clearTimer();
      // Transition the progress message to a "waiting for response" state
      // rather than removing it.  This keeps visual feedback visible until the
      // 'message' event arrives with the RA summary.  AV progress items are
      // captured in the completed state; non-AV progress shows "Preparing
      // response".  Final cleanup happens in clearResearchAssistantIsProcessing.
      dispatch(markWorkflowCompletedProgress());
    },
    [clearTimer, dispatch]
  );

  useEffect(() => {
    if (!currentSocket) return;

    currentSocket.on('workflow_started', onWorkflowStarted);
    currentSocket.on('workflow_completed', onWorkflowCompleted);

    return () => {
      currentSocket.off('workflow_started', onWorkflowStarted);
      currentSocket.off('workflow_completed', onWorkflowCompleted);
      clearTimer();
    };
  }, [currentSocket, onWorkflowStarted, onWorkflowCompleted, clearTimer]);

  return { startTimer };
};
